import { useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import type { CalibrationParameter } from "@/api/calibration";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/** Sufixo por unidade. `flag` não tem: 0 ou 1 não pede legenda. */
const SUFFIX: Record<string, string> = {
  percent: "%",
  hours: "h",
  days: "d",
  fte: "FTE",
  currency: "R$",
  ratio: "×",
};

export interface ParameterPanelProps {
  parameters: CalibrationParameter[];
  overrides: Record<string, number>;
  onChange: (key: string, value: number) => void;
  /** Conteúdo a mais no topo de uma família, como a sugestão do Catálogo WRICEF nas extensões. */
  extras?: Partial<Record<string, ReactNode>>;
}

/**
 * Os 55 parâmetros, agrupados por família.
 *
 * **`kind` separa o que é nosso do que é do projeto.** `rate` é curadoria da
 * casa e vale para todo cliente; `quantity` é levantamento deste projeto, e o
 * default zero é formulário em branco, não decisão. A tela diz isso no selo,
 * senão a pessoa não sabe se está corrigindo a metodologia ou preenchendo um
 * dado do cliente.
 *
 * **`is_hypothesis` é a marca que não pode se perder.** Os percentuais do fator
 * myGROW são premissa declarada na origem e são eles que produzem a economia
 * que vai ao cliente. Premissa apresentada como medição é exatamente o que o
 * Diagnóstico existe para não fazer.
 */
export function ParameterPanel({
  parameters,
  overrides,
  onChange,
  extras,
}: ParameterPanelProps) {
  const { t } = useTranslation();

  const families = useMemo(() => {
    const grouped = new Map<string, CalibrationParameter[]>();
    for (const p of parameters) {
      const bucket = grouped.get(p.family) ?? [];
      bucket.push(p);
      grouped.set(p.family, bucket);
    }
    return Array.from(grouped.entries());
  }, [parameters]);

  return (
    <div className="flex flex-col gap-5">
      {families.map(([family, params]) => (
        <section key={family} className="flex flex-col gap-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t(family)}
          </h3>
          {extras?.[family]}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {params.map((param) => {
              const value = overrides[param.param_key] ?? param.default_value;
              return (
                <div key={param.param_key} className="flex flex-col gap-1">
                  <Label
                    htmlFor={`param-${param.param_key}`}
                    className="flex items-center gap-1.5 text-xs"
                  >
                    <span className="truncate">{param.label}</span>
                    {param.is_hypothesis ? (
                      <Badge
                        variant="outline"
                        className="shrink-0 text-[10px]"
                        title={t("Declared assumption, not a measurement")}
                      >
                        {t("assumption")}
                      </Badge>
                    ) : null}
                    {param.kind === "quantity" ? (
                      <Badge
                        variant="secondary"
                        className="shrink-0 text-[10px]"
                      >
                        {t("this project")}
                      </Badge>
                    ) : null}
                  </Label>
                  <div className="flex items-center gap-1.5">
                    <Input
                      id={`param-${param.param_key}`}
                      type="number"
                      value={value}
                      onChange={(e) =>
                        onChange(param.param_key, Number(e.target.value))
                      }
                      className="h-8"
                    />
                    {SUFFIX[param.unit] ? (
                      <span className="w-8 shrink-0 text-xs text-muted-foreground">
                        {SUFFIX[param.unit]}
                      </span>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
